import { Search } from "lucide-react";
import React, { ReactNode, useContext, useState } from "react";
import {
  ActionIcon,
  Button,
  Space,
  Anchor,
  Breadcrumbs,
  Divider,
  Group,
} from "@mantine/core";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import ErrorPage from "../../../common/pages/ErrorPage";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import PageContainer from "../../../themes/components/PageContainer";
import { AuthContext } from "../../../providers/AuthProvider";
import PageHeading from "../../../themes/components/PageHeading";
import useTagDetailsApiService from "../services/api/TagDetailsApiService";
import TagTableEmptyStateContainer from "../components/TagTableEmptyStateContainer";
import TagAssigneeTable from "../components/TagAssigneeTable";
import TagAssignee from "../services/model/TagAssignee";
import UnAssignTagButton from "../elements/UnAssignTagButton";
import NotFound from "../../../common/pages/NotFound";
import { AccessPermissionContext } from "../../../providers/AccessPermissionsProvider";

const TagDetailsPage: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { hasPermissions } = React.useContext(AccessPermissionContext);
  const { id } = useParams();
  const [tagAssignee, setTagAssignee] = useState<TagAssignee[]>([]);
  const [searchBarOpened, setSearchBarOpened] = useState(false);
  const [opened, setOpened] = useState(false);
  const { isLoading, isError, error, data } = useTagDetailsApiService(
    Number(id)
  );

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  if (!hasPermissions) {
    return <NotFound />;
  }

  if (!id) {
    return <Navigate replace to="/employees" />; // prevent manual navigation to this page
  }

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  if (!data) {
    return <TagTableEmptyStateContainer />;
  }

  const breadcrumbItems = [
    { title: "Employees", to: "/employees" },
    { title: "Tags", to: "/employees" },
    {
      title: `${data.name}`,
      to: `${data.name.replaceAll(" ", "-").toLowerCase()}`,
    },
  ].map((item) => (
    <Anchor component={Link} to={item.to} key={Math.random()} color="dark">
      {item.title}
    </Anchor>
  ));

  const renderHeaderButtons = (): ReactNode => (
    <Group spacing="xl">
      <Button
        variant="outline"
        type="button"
        color="jem-theme"
        onClick={() => navigate(-1)}
      >
        Back
      </Button>

      {tagAssignee.length > 0 && (
        <UnAssignTagButton
          employees={tagAssignee.map((it) => it.id)}
          tagId={Number(id)}
        />
      )}

      <Button
        variant="filled"
        type="button"
        color="jem-theme"
        onClick={() => setOpened(true)}
      >
        Add Employees
      </Button>
    </Group>
  );

  return (
    <PageContainer>
      <PageHeading title="Manage Tags">{renderHeaderButtons()}</PageHeading>
      <Space h="xl" />
      <Group position="apart">
        <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
        <div>
          <ActionIcon
            key="search"
            onClick={() => setSearchBarOpened(!searchBarOpened)}
          >
            <Search size={18} />
          </ActionIcon>
        </div>
      </Group>
      <Divider />
      <TagAssigneeTable
        id={Number(id)}
        selection={tagAssignee}
        onSelection={setTagAssignee}
        isFormDrawerOpen={opened}
        toggleFormDrawer={setOpened}
      />
    </PageContainer>
  );
};

export default TagDetailsPage;
